import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { InstancesList } from '../components/InstancesList'
import { useApiData } from '../hooks/useApiData'
import { colors, spacing, borderRadius, typography, transitions } from '../styles/tokens'

function ConsolePage() {
  const { token, subscription, user, api, clearAuth } = useAuthStore()
  const navigate = useNavigate()
  const { data, loading, error, refreshData, getAdditionalData } = useApiData(token, api)
  
  // Configuración temporal - ocultar funcionalidad data2 hasta que esté implementada
  const SHOW_DATA2_BUTTON = false



  const handleLogout = () => {
    clearAuth()
    navigate('/login')
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: spacing['2xl']
    }}>
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: `${spacing.lg} ${spacing['2xl']}`,
        borderBottom: `1px solid ${colors.border.main}`,
        backgroundColor: colors.background.header,
        opacity: 0.8
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: spacing['2xl'],
          fontSize: typography.fontSize.sm,
          color: colors.text.muted
        }}>
          <h1 style={{ 
            margin: 0, 
            fontSize: typography.fontSize.xl, 
            color: colors.text.secondary,
            fontWeight: typography.fontWeight.normal
          }}>
            WHIN Console
          </h1>
          <span>Plan: <strong style={{ color: colors.text.tertiary }}>{subscription}</strong></span>
          <span>User: <strong style={{ color: colors.text.tertiary }}>{user}</strong></span>
          <span>API: <strong style={{ color: colors.text.tertiary }}>{api}</strong></span>
        </div>
        <button 
          onClick={handleLogout}
          style={{
            padding: `${spacing.sm} ${spacing.lg}`,
            backgroundColor: colors.background.button,
            color: colors.text.secondary,
            border: `1px solid ${colors.border.subtle}`,
            borderRadius: borderRadius.md,
            fontSize: typography.fontSize.sm,
            cursor: 'pointer',
            transition: transitions.fast
          }}
          onMouseEnter={(e) => {
            const target = e.target as HTMLButtonElement
            target.style.backgroundColor = colors.background.buttonHover
          }}
          onMouseLeave={(e) => {
            const target = e.target as HTMLButtonElement
            target.style.backgroundColor = colors.background.button
          }}
          aria-label="Sign out and return to login"
        >
          Sign Out
        </button>
      </header>

      <main style={{
        display: 'flex',
        minHeight: 'calc(100vh - 80px)',
        backgroundColor: colors.background.main
      }}>
        <aside style={{
          width: '200px',
          backgroundColor: colors.background.sidebar,
          borderRight: `1px solid ${colors.border.main}`,
          padding: spacing.lg,
          opacity: 0.7
        }}>
          <section>
            <h2 style={{
              fontSize: typography.fontSize.md,
              color: colors.text.disabled,
              marginBottom: spacing.lg,
              fontWeight: typography.fontWeight.normal
            }}>
              Actions
            </h2>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.sm
            }}>
              <button
                onClick={refreshData}
                disabled={loading}
                style={{
                  padding: spacing.sm,
                  backgroundColor: colors.background.input,
                  color: colors.text.tertiary,
                  border: `1px solid ${colors.border.input}`,
                  borderRadius: borderRadius.sm,
                  fontSize: typography.fontSize.sm,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1,
                  transition: transitions.fast
                }}
                aria-describedby="data-help"
              >
                {loading ? 'Loading...' : 'Refresh'}
              </button>
              
              {SHOW_DATA2_BUTTON && (
                <button
                  onClick={getAdditionalData}
                  disabled={loading}
                  style={{
                    padding: spacing.sm,
                    backgroundColor: colors.background.input,
                    color: colors.text.tertiary,
                    border: `1px solid ${colors.border.input}`,
                    borderRadius: borderRadius.sm,
                    fontSize: typography.fontSize.sm,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    transition: transitions.fast
                  }}
                  aria-describedby="data2-help"
                >
                  {loading ? 'Loading...' : 'Get Additional Data'}
                </button>
              )}
            </div>
            
            <div id="data-help" className="sr-only">
              Refreshes the main data from the backend
            </div>
            {SHOW_DATA2_BUTTON && (
              <div id="data2-help" className="sr-only">
                Fetches additional data from the backend
              </div>
            )}
          </section>
        </aside>

        <section style={{
          flex: 1,
          padding: spacing['2xl'],
          backgroundColor: colors.background.main
        }}>
          <h2 style={{
            fontSize: typography.fontSize['3xl'],
            color: colors.text.primary,
            marginBottom: spacing.xl,
            fontWeight: typography.fontWeight.bold
          }}>
            Instances
          </h2>
          <div 
            style={{
              backgroundColor: colors.background.card,
              border: `1px solid ${colors.border.subtle}`,
              borderRadius: borderRadius.lg,
              padding: spacing.xl,
              textAlign: 'left',
              minHeight: '400px',
              overflowX: 'auto',
              opacity: loading ? 0.7 : 1
            }}
            role="region"
            aria-live="polite"
            aria-label="Data results area"
          >
            {loading && (
              <div>
                <p>⏳ Loading...</p>
              </div>
            )}
            
            {error && !loading && (
              <div style={{
                color: colors.error.text,
                backgroundColor: colors.error.background,
                border: `1px solid ${colors.error.border}`,
                borderRadius: borderRadius.md,
                padding: spacing.lg
              }} role="alert">
                <h3 style={{ margin: `0 0 ${spacing.sm} 0` }}>❌ Error</h3>
                <p style={{ margin: 0 }}>{error}</p>
              </div>
            )}
            
            {data && !loading && !error && (
              <InstancesList data={data} />
            )}
            
            {!data && !loading && !error && (
              <div style={{ color: colors.text.muted, fontStyle: 'italic' }}>
                <p>Loading initial data...</p>
                <p>Please wait while we fetch your information.</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  )
}

export default ConsolePage
